<script setup lang="ts">
import { AntlerClasses, installAntlerComponent } from "@/Utils/component";

type Props = {
    reviewUpdate: object;
    reviewApiPath: string;
    reviewUserId: string;
    isOverview?: boolean;
    latestRating: string | number;
    href?: string;
    canEdit?: boolean;
};

const {
    reviewUpdate,
    reviewApiPath,
    reviewUserId,
    isOverview = false,
    latestRating,
    href = undefined,
    canEdit = true,
} = defineProps<Props>();

import { useModal } from "@/Utils/use-modal";
import {
    apiPathUrl,
    isCurrentUserById,
    isGuest,
    isLoggedIn,
    isVerified,
    mustBeLogin,
    mustBeOwner,
    mustBeVerified,
} from "@/Utils/helpers";
import { computed } from "vue";
import TheReviewUpdateForm from "@/Components/App/review-updates/TheReviewUpdateForm.vue";
import { router } from "@inertiajs/vue3";
import { toast } from "vue-sonner";
import AppText from "@/Components/Shared/text/AppText.vue";
import AppGrade from "@/Components/App/grades/AppGrade.vue";
import AppArticle from "@/Components/Shared/article/AppArticle.vue";
import AppList from "@/Components/Shared/list/AppList.vue";
import AppListItem from "@/Components/Shared/list/AppListItem.vue";
import AppTimelineOptions from "@/Components/App/feed/AppTimelineOptions.vue";
import TheFlagForm from "@/Components/App/flags/TheFlagForm.vue";
import useEmitter from "@/Composables/useEmitter";
const { emitter } = useEmitter();

const emit = defineEmits(["updateitem"]);

const classes: AntlerClasses<Props> = {
    base: "relative flex flex-col bg-white border gap-2 p-2 rounded border border-gray-lighter",
};
const { aClass } = installAntlerComponent("review-update-card", {}, classes);

// computed
const isOwner = computed(() => {
    return isLoggedIn ? isCurrentUserById(reviewUserId) : false;
});

// methods
const { openModal } = useModal();

const edit = () => {
    if (isGuest.value) {
        mustBeLogin();
        return;
    }

    if (!isVerified.value) {
        mustBeVerified();
        return;
    }

    openModal({
        component: TheReviewUpdateForm,
        title: "Update aanpassen",
        props: {
            reviewUpdate: reviewUpdate,
            reviewApiPath: reviewApiPath,
            latestRating: latestRating,
            canEditRating: canEdit,
        },
    });
};

const flag = () => {
    if (isGuest.value) {
        mustBeLogin();
        return;
    }

    if (!isVerified.value) {
        mustBeVerified();
        return;
    }

    openModal({
        component: TheFlagForm,
        title: "Review Update rapporteren",
        props: {
            itemId: reviewUpdate.id,
            apiPath: reviewUpdate._path,
            type: "review-update",
        },
    });
};

const destroy = () => {
    if (isGuest.value) {
        mustBeLogin();
        return;
    }

    if (!isVerified.value) {
        mustBeVerified();
        return;
    }

    if (!isOwner.value) {
        mustBeOwner();
        return;
    }

    if (confirm("Weet je zeker dat je deze update wilt verwijderen?")) {
        router.delete(apiPathUrl(reviewUpdate._path), {
            preserveScroll: true,
            onSuccess: () => {
                toast.success("De update is verwijderd");

                // delete item from timeline
                emitter.emit("deletedItem", reviewUpdate.id);
            },
            onError: () => {
                toast.error(
                    "Er ging iets mis bij het verwijderen van de update",
                );
            },
        });
    }
};

const prosAndCons = computed(() => {
    const prosAndCons: any = [];
    if (reviewUpdate.pros && reviewUpdate.pros.length) {
        reviewUpdate.pros.forEach((pro: any) => {
            prosAndCons.push({
                type: "pro",
                text: pro,
            });
        });
    }

    if (reviewUpdate.cons && reviewUpdate.cons.length) {
        reviewUpdate.cons.forEach((con: any) => {
            prosAndCons.push({
                type: "con",
                text: con,
            });
        });
    }

    return prosAndCons;
});
</script>

<template>
    <div :class="aClass()">
        <component
            :is="href ? 'AppLink' : 'div'"
            :weight="href ? 'regular' : null"
            :href="href"
            class="flex flex-col gap-2 p-2 pr-8"
        >
            <div class="flex gap-4 sm:gap-6">
                <AppGrade :grade="reviewUpdate.rating" />

                <div class="grow">
                    <AppArticle
                        v-if="isOverview"
                        variant="noMaxWidth"
                        class="line-clamp-2"
                        size="small"
                    >
                        {{ reviewUpdate.excerpt }}
                    </AppArticle>
                    <AppArticle
                        v-else
                        variant="noMaxWidth"
                    >
                        {{ reviewUpdate.content }}
                    </AppArticle>

                    <AppList
                        v-if="prosAndCons.length"
                        class="mt-3 w-full"
                        variant="double"
                    >
                        <template v-if="isOverview">
                            <AppListItem
                                v-for="item in prosAndCons.slice(0, 3)"
                                :icon="item.type === 'pro' ? 'check' : 'cross'"
                                icon-size="xsmall"
                            >
                                {{ item.text }}
                            </AppListItem>
                            <AppListItem v-if="prosAndCons.length > 3">
                                ...
                            </AppListItem>
                        </template>
                        <template v-else>
                            <AppListItem
                                v-for="item in prosAndCons"
                                :icon="item.type === 'pro' ? 'check' : 'cross'"
                                icon-size="xsmall"
                            >
                                {{ item.text }}
                            </AppListItem>
                        </template>
                    </AppList>

                    <AppText
                        color="gray"
                        size="small"
                        class="mt-3"
                    >
                        <span v-if="isOverview">Laatste update: </span>
                        {{ reviewUpdate.created }}
                    </AppText>
                </div>
            </div>
        </component>

        <div class="absolute top-4 right-4">
            <AppTimelineOptions
                :is-owner="isOwner"
                @edit="edit"
                @destroy="destroy"
                @flag="flag"
            />
        </div>
    </div>
</template>
